import type { AudioFrame } from "@/utils/arcade/beat";
import type { ArcadePalette } from "@/utils/arcade/theme";

// The arcade's shared look. Glow comes from pre-rendered sprites and a
// downsampled bloom pass instead of shadowBlur, which is what keeps six busy
// games at 60fps on phones. Everything here degrades to plain shapes when a
// canvas cannot be created (tests, very old browsers).

export type Ctx2D = CanvasRenderingContext2D;
type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;
export type CanvasFactory = (width: number, height: number) => AnyCanvas | null;

export const defaultCanvasFactory: CanvasFactory = (width, height) => {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  if (typeof document !== "undefined") {
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    return canvas;
  }
  return null;
};

export const noCanvas: CanvasFactory = () => null;

export function context2d(canvas: AnyCanvas | null): Ctx2D | null {
  if (!canvas) return null;
  try {
    return (canvas.getContext("2d") as unknown as Ctx2D | null) ?? null;
  } catch {
    return null;
  }
}

export interface ArtKit {
  readonly cover: CanvasImageSource | null;
  coverBlur(): CanvasImageSource | null;
  glow(color: string): CanvasImageSource | null;
  scratch(key: string, width: number, height: number): { canvas: AnyCanvas; ctx: Ctx2D } | null;
  make: CanvasFactory;
}

const GLOW_SIZE = 96;

export function createArtKit(getCover: () => CanvasImageSource | null, make: CanvasFactory = defaultCanvasFactory): ArtKit {
  const glows = new Map<string, AnyCanvas | null>();
  const scratches = new Map<string, { canvas: AnyCanvas; ctx: Ctx2D }>();
  let blurFor: CanvasImageSource | null = null;
  let blurred: AnyCanvas | null = null;

  return {
    get cover() {
      return getCover();
    },
    make,
    coverBlur() {
      const cover = getCover();
      if (!cover) return null;
      if (cover === blurFor) return blurred;
      blurFor = cover;
      // Two downsample steps and an upscale make a soft blur without filters.
      const tiny = make(10, 10);
      const small = make(40, 40);
      const tinyCtx = context2d(tiny);
      const smallCtx = context2d(small);
      if (!tiny || !small || !tinyCtx || !smallCtx) return (blurred = null);
      smallCtx.imageSmoothingQuality = "high";
      smallCtx.drawImage(cover, 0, 0, 40, 40);
      tinyCtx.imageSmoothingQuality = "high";
      tinyCtx.drawImage(small, 0, 0, 10, 10);
      smallCtx.clearRect(0, 0, 40, 40);
      smallCtx.drawImage(tiny, 0, 0, 40, 40);
      blurred = small;
      return blurred;
    },
    glow(color) {
      if (glows.has(color)) return glows.get(color) ?? null;
      const canvas = make(GLOW_SIZE, GLOW_SIZE);
      const ctx = context2d(canvas);
      if (!canvas || !ctx) {
        glows.set(color, null);
        return null;
      }
      const half = GLOW_SIZE / 2;
      const gradient = ctx.createRadialGradient(half, half, 0, half, half, half);
      gradient.addColorStop(0, "rgba(255,255,255,1)");
      gradient.addColorStop(0.12, color);
      gradient.addColorStop(0.4, withAlpha(color, 0.35));
      gradient.addColorStop(1, withAlpha(color, 0));
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, GLOW_SIZE, GLOW_SIZE);
      glows.set(color, canvas);
      return canvas;
    },
    scratch(key, width, height) {
      const w = Math.max(1, Math.round(width));
      const h = Math.max(1, Math.round(height));
      const existing = scratches.get(key);
      if (existing && existing.canvas.width === w && existing.canvas.height === h) return existing;
      const canvas = make(w, h);
      const ctx = context2d(canvas);
      if (!canvas || !ctx) return null;
      const entry = { canvas, ctx };
      scratches.set(key, entry);
      return entry;
    },
  };
}

// "hsl(210 80% 60%)" -> "hsl(210 80% 60% / a)". Other colour strings pass through.
export function withAlpha(color: string, alpha: number): string {
  const hsl = color.match(/^hsl\(([^/)]+)(?:\/[^)]*)?\)$/);
  if (hsl) return `hsl(${hsl[1].trim()} / ${alpha})`;
  const rgb = color.match(/^rgba?\(([^)]+)\)$/);
  if (rgb) {
    const parts = rgb[1].split(/[\s,/]+/).filter(Boolean).slice(0, 3);
    return `rgba(${parts.join(", ")}, ${alpha})`;
  }
  return color;
}

// Ceiling on every glow sprite. Glows stack additively, so a few overlapping
// at full strength wash the frame out; players found that tiring to look at.
export const GLOW_CEILING = 0.55;

export function drawGlow(ctx: Ctx2D, art: ArtKit, color: string, x: number, y: number, radius: number, alpha = 1) {
  alpha *= GLOW_CEILING;
  if (radius <= 0 || alpha <= 0) return;
  const sprite = art.glow(color);
  const previousAlpha = ctx.globalAlpha;
  const previousOp = ctx.globalCompositeOperation;
  ctx.globalCompositeOperation = "lighter";
  ctx.globalAlpha = previousAlpha * Math.min(1, alpha);
  if (sprite) {
    ctx.drawImage(sprite, x - radius, y - radius, radius * 2, radius * 2);
  } else {
    ctx.fillStyle = withAlpha(color, 0.35);
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = previousAlpha;
  ctx.globalCompositeOperation = previousOp;
}

// A neon stroke: a wide faint pass under a thin bright one.
export function strokeNeon(ctx: Ctx2D, trace: () => void, color: string, width: number, intensity = 1) {
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.globalCompositeOperation = "lighter";
  ctx.strokeStyle = withAlpha(color, 0.16 * intensity);
  ctx.lineWidth = width * 5;
  trace();
  ctx.stroke();
  ctx.strokeStyle = withAlpha(color, 0.45 * intensity);
  ctx.lineWidth = width * 2.2;
  trace();
  ctx.stroke();
  ctx.globalCompositeOperation = "source-over";
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  trace();
  ctx.stroke();
  ctx.restore();
}

// The release artwork blown up and blurred to fill the scene.
export function drawCoverBackdrop(ctx: Ctx2D, art: ArtKit, palette: ArcadePalette, width: number, height: number, options: { alpha?: number; zoom?: number; drift?: number } = {}) {
  const { alpha = 0.55, zoom = 1.25, drift = 0 } = options;
  ctx.fillStyle = palette.backgroundDeep;
  ctx.fillRect(0, 0, width, height);
  const blur = art.coverBlur();
  if (blur) {
    const size = Math.max(width, height) * zoom;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(blur, (width - size) / 2 + Math.sin(drift) * size * 0.03, (height - size) / 2 + Math.cos(drift * 0.7) * size * 0.03, size, size);
    ctx.restore();
  }
  const shade = ctx.createLinearGradient(0, 0, 0, height);
  shade.addColorStop(0, withAlpha(palette.backgroundDeep, 0.55));
  shade.addColorStop(0.5, withAlpha(palette.backgroundDeep, 0.25));
  shade.addColorStop(1, withAlpha(palette.backgroundDeep, 0.8));
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, width, height);
}

// The cover art as a retrowave sun: a glowing disc with bands cut through its
// lower half that slide down with the music.
export function drawCoverSun(ctx: Ctx2D, art: ArtKit, palette: ArcadePalette, x: number, y: number, radius: number, phase: number, pulse: number) {
  drawGlow(ctx, art, palette.primary, x, y, radius * 2.6, 0.55 + pulse * 0.35);
  drawGlow(ctx, art, palette.accent, x, y, radius * 1.5, 0.25 + pulse * 0.2);
  const layer = art.scratch("sun", radius * 2 + 4, radius * 2 + 4);
  const target = layer ? layer.ctx : ctx;
  const ox = layer ? radius + 2 : x;
  const oy = layer ? radius + 2 : y;
  if (layer) layer.ctx.clearRect(0, 0, layer.canvas.width, layer.canvas.height);
  target.save();
  target.beginPath();
  target.arc(ox, oy, radius, 0, Math.PI * 2);
  target.clip();
  const cover = art.cover;
  if (cover) {
    target.drawImage(cover, ox - radius, oy - radius, radius * 2, radius * 2);
  }
  const tint = target.createLinearGradient(0, oy - radius, 0, oy + radius);
  tint.addColorStop(0, withAlpha(palette.accent, cover ? 0.35 : 1));
  tint.addColorStop(0.55, withAlpha(palette.primary, cover ? 0.4 : 1));
  tint.addColorStop(1, withAlpha(palette.secondary, cover ? 0.65 : 1));
  target.fillStyle = tint;
  target.fillRect(ox - radius, oy - radius, radius * 2, radius * 2);
  target.restore();
  if (layer) {
    layer.ctx.save();
    layer.ctx.globalCompositeOperation = "destination-out";
    const band = radius * 0.16;
    const offset = (phase % 1) * band;
    for (let i = 0; i < 8; i++) {
      const top = oy + radius * 0.08 + i * band + offset;
      const thickness = band * (0.12 + i * 0.07);
      layer.ctx.fillRect(0, top, layer.canvas.width, thickness);
    }
    layer.ctx.restore();
    ctx.drawImage(layer.canvas as CanvasImageSource, x - radius - 2, y - radius - 2);
  }
}

// The cover as a spinning record, with grooves, a moving sheen and a rim light.
export function drawVinyl(ctx: Ctx2D, art: ArtKit, palette: ArcadePalette, x: number, y: number, radius: number, rotation: number, glow = 0.6) {
  if (glow > 0) drawGlow(ctx, art, palette.primary, x, y, radius * 1.9, glow);
  ctx.save();
  ctx.translate(x, y);
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  const body = ctx.createRadialGradient(0, 0, radius * 0.2, 0, 0, radius);
  body.addColorStop(0, "#1a1a1f");
  body.addColorStop(1, "#050507");
  ctx.fillStyle = body;
  ctx.fill();
  if (radius > 14) {
    ctx.lineWidth = Math.max(0.5, radius * 0.01);
    for (let groove = 0.52; groove < 0.97; groove += 0.045) {
      ctx.strokeStyle = `rgba(255,255,255,${0.04 + (groove * 13) % 0.05})`;
      ctx.beginPath();
      ctx.arc(0, 0, radius * groove, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.save();
  ctx.rotate(rotation * 0.15 - 0.6);
  ctx.globalCompositeOperation = "lighter";
  const sheen = ctx.createLinearGradient(-radius, -radius, radius, radius);
  sheen.addColorStop(0.35, "rgba(255,255,255,0)");
  sheen.addColorStop(0.5, withAlpha(palette.accent, 0.22));
  sheen.addColorStop(0.65, "rgba(255,255,255,0)");
  ctx.fillStyle = sheen;
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.rotate(rotation);
  const label = radius * 0.44;
  ctx.beginPath();
  ctx.arc(0, 0, label, 0, Math.PI * 2);
  ctx.save();
  ctx.clip();
  if (art.cover) {
    ctx.drawImage(art.cover, -label, -label, label * 2, label * 2);
  } else {
    const fill = ctx.createRadialGradient(-label * 0.3, -label * 0.3, 0, 0, 0, label);
    fill.addColorStop(0, palette.accent);
    fill.addColorStop(1, palette.primary);
    ctx.fillStyle = fill;
    ctx.fillRect(-label, -label, label * 2, label * 2);
  }
  ctx.restore();
  ctx.beginPath();
  ctx.arc(0, 0, Math.max(1.5, radius * 0.035), 0, Math.PI * 2);
  ctx.fillStyle = "#050507";
  ctx.fill();
  ctx.restore();
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.strokeStyle = withAlpha(palette.primary, 0.55);
  ctx.lineWidth = Math.max(1, radius * 0.025);
  ctx.stroke();
  ctx.restore();
}

// The cover as a plain disc, for small sprites.
export function drawCoverDisc(ctx: Ctx2D, art: ArtKit, palette: ArcadePalette, x: number, y: number, radius: number, rotation = 0) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.clip();
  if (art.cover) {
    ctx.drawImage(art.cover, -radius, -radius, radius * 2, radius * 2);
  } else {
    const fill = ctx.createRadialGradient(-radius * 0.3, -radius * 0.3, 0, 0, 0, radius);
    fill.addColorStop(0, palette.accent);
    fill.addColorStop(1, palette.primary);
    ctx.fillStyle = fill;
    ctx.fillRect(-radius, -radius, radius * 2, radius * 2);
  }
  ctx.restore();
}

export function drawLightRays(ctx: Ctx2D, x: number, y: number, count: number, rotation: number, color: string, alpha: number, length: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.globalCompositeOperation = "lighter";
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, length);
  gradient.addColorStop(0, withAlpha(color, alpha));
  gradient.addColorStop(1, withAlpha(color, 0));
  ctx.fillStyle = gradient;
  const spread = (Math.PI * 2) / count;
  for (let i = 0; i < count; i++) {
    const a = i * spread;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, length, a, a + spread * 0.35);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

export interface Star {
  x: number;
  y: number;
  z: number;
  twinkle: number;
}

export function createStars(rng: () => number, count: number): Star[] {
  return Array.from({ length: count }, () => ({ x: rng(), y: rng(), z: 0.2 + rng() * 0.8, twinkle: rng() * Math.PI * 2 }));
}

export function drawStars(ctx: Ctx2D, stars: Star[], width: number, height: number, time: number, color: string, drift = { x: 0, y: 0 }, treble = 0) {
  ctx.save();
  ctx.fillStyle = color;
  for (const star of stars) {
    const x = (((star.x + drift.x * star.z) % 1) + 1) % 1;
    const y = (((star.y + drift.y * star.z) % 1) + 1) % 1;
    ctx.globalAlpha = (0.25 + 0.6 * star.z) * (0.6 + 0.4 * Math.sin(time * 2 + star.twinkle)) * (0.8 + treble * 0.6);
    const size = 0.6 + star.z * 1.6;
    ctx.fillRect(x * width, y * height, size, size);
  }
  ctx.restore();
}

// A nebula painted once from seeded blobs in the song's colours.
export function createNebula(art: ArtKit, palette: ArcadePalette, rng: () => number): AnyCanvas | null {
  const size = 256;
  const canvas = art.make(size, size);
  const ctx = context2d(canvas);
  if (!canvas || !ctx) return null;
  ctx.fillStyle = palette.backgroundDeep;
  ctx.fillRect(0, 0, size, size);
  ctx.globalCompositeOperation = "lighter";
  const colors = [palette.primary, palette.secondary, palette.accent, palette.lanes[3] ?? palette.primary];
  for (let i = 0; i < 14; i++) {
    const x = rng() * size;
    const y = rng() * size;
    const r = size * (0.15 + rng() * 0.35);
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
    gradient.addColorStop(0, withAlpha(colors[i % colors.length], 0.16 + rng() * 0.12));
    gradient.addColorStop(1, withAlpha(colors[i % colors.length], 0));
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  }
  return canvas;
}

export function drawSpectrumRing(ctx: Ctx2D, audio: AudioFrame, x: number, y: number, inner: number, reach: number, rotation: number, colors: [string, string]) {
  const bars = audio.spectrum.length * 2;
  ctx.save();
  ctx.translate(x, y);
  ctx.globalCompositeOperation = "lighter";
  ctx.lineCap = "round";
  const width = Math.max(2, ((Math.PI * 2 * inner) / bars) * 0.55);
  for (let i = 0; i < bars; i++) {
    const mirrored = i < bars / 2 ? i : bars - 1 - i;
    const level = audio.spectrum[mirrored] ?? 0;
    const a = (i / bars) * Math.PI * 2 + rotation;
    const outer = inner + reach * (0.12 + level);
    ctx.strokeStyle = withAlpha(i % 2 === 0 ? colors[0] : colors[1], 0.35 + level * 0.6);
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * inner, Math.sin(a) * inner);
    ctx.lineTo(Math.cos(a) * outer, Math.sin(a) * outer);
    ctx.stroke();
  }
  ctx.restore();
}

export function drawBeatFlash(ctx: Ctx2D, width: number, height: number, color: string, amount: number) {
  if (amount <= 0.01) return;
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  const gradient = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, Math.max(width, height) * 0.75);
  gradient.addColorStop(0, withAlpha(color, Math.min(0.06, amount * 0.06)));
  gradient.addColorStop(1, withAlpha(color, 0));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

// Game text: a dark outline for contrast over any scene, a white fill and a
// hairline of the colour. The old version stroked a wide additive halo under
// the letters, which smeared into a ghost copy and made banners hard to read.
export function glowText(ctx: Ctx2D, text: string, x: number, y: number, size: number, color: string, alpha = 1) {
  ctx.save();
  ctx.font = `800 ${Math.round(size)}px Hanson, Poppins, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = "source-over";
  ctx.lineWidth = Math.max(2, size * 0.14);
  ctx.strokeStyle = "rgba(0,0,0,0.6)";
  ctx.strokeText(text, x, y);
  ctx.fillStyle = "#fff";
  ctx.fillText(text, x, y);
  ctx.lineWidth = Math.max(1, size * 0.035);
  ctx.strokeStyle = withAlpha(color, 0.85);
  ctx.strokeText(text, x, y);
  ctx.restore();
}

export interface PostFx {
  apply(ctx: Ctx2D, source: CanvasImageSource, pixelWidth: number, pixelHeight: number, audio: AudioFrame, bloom: number): void;
}

// Full frame bloom: the finished frame is shrunk twice and added back on top,
// so everything bright bleeds light. A vignette pulls the eye to the middle.
export function createPostFx(make: CanvasFactory = defaultCanvasFactory): PostFx {
  let mid: AnyCanvas | null = null;
  let low: AnyCanvas | null = null;
  let vignette: AnyCanvas | null = null;
  let key = "";

  const prepare = (width: number, height: number) => {
    const next = `${width}x${height}`;
    if (next === key) return;
    key = next;
    mid = make(Math.max(8, width / 4), Math.max(8, height / 4));
    low = make(Math.max(4, width / 12), Math.max(4, height / 12));
    vignette = make(Math.max(8, width / 4), Math.max(8, height / 4));
    const vctx = context2d(vignette);
    if (vignette && vctx) {
      const w = vignette.width;
      const h = vignette.height;
      const gradient = vctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.hypot(w, h) * 0.6);
      gradient.addColorStop(0, "rgba(0,0,0,0)");
      gradient.addColorStop(1, "rgba(0,0,0,0.55)");
      vctx.fillStyle = gradient;
      vctx.fillRect(0, 0, w, h);
    }
  };

  return {
    apply(ctx, source, pixelWidth, pixelHeight, audio, bloom) {
      prepare(pixelWidth, pixelHeight);
      const midCtx = context2d(mid);
      const lowCtx = context2d(low);
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      if (bloom > 0 && mid && low && midCtx && lowCtx) {
        midCtx.globalCompositeOperation = "copy";
        midCtx.drawImage(source, 0, 0, mid.width, mid.height);
        lowCtx.globalCompositeOperation = "copy";
        lowCtx.drawImage(mid, 0, 0, low.width, low.height);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.globalCompositeOperation = "lighter";
        // Bloom is a soft halo, not a second exposure: keep it well under half
        // the frame's own brightness even on the beat.
        ctx.globalAlpha = bloom * (0.16 + audio.pulse * 0.06);
        ctx.drawImage(low as CanvasImageSource, 0, 0, pixelWidth, pixelHeight);
        ctx.globalAlpha = bloom * 0.08;
        ctx.drawImage(mid as CanvasImageSource, 0, 0, pixelWidth, pixelHeight);
      }
      if (vignette) {
        ctx.globalCompositeOperation = "source-over";
        ctx.globalAlpha = 1;
        ctx.drawImage(vignette as CanvasImageSource, 0, 0, pixelWidth, pixelHeight);
      }
      ctx.restore();
    },
  };
}
